<template>
  <div class="cds">
    <ContentBlock>
      <div slot="instructions">
        <h5>{{ $t('Content Distribution Upload') }}</h5>
        <p>
          {{ $t('Our Content Distribution System (CDS) allows us to securely distribute, manage and track the usage of your title.') }}
          <i18n
            path="Once uploaded, we will review your title within 3 to 5 days to ensure it works on our platform and follows our {guidelinelink}."
          >
            <a
              class="link-style"
              slot="guidelinelink"
              href="https://svr.li/k/361"
              target="_blank"
              rel="noopener"
            >{{ $t("Content Guidelines") }}</a>
          </i18n>
        </p>
        <p
          v-if="contentOwnership.status !== 'active'"
        >{{ $t('You can continue to the next steps but you won’t be able to publish your title until it’s approved.') }}</p>
        <i18n tag="p" path="Please contact {supportEmail} if you have any questions.">
          <a
            class="link-style"
            slot="supportEmail"
            href="mailto:springboardvr@deployreality.com"
            target="_blank"
          >{{ $t("support@springboardvr.com") }}</a>
        </i18n>
      </div>
      <div>
        <div class="status">
          <div class="label">{{ $t('Content Distribution Upload Status') }}</div>
          <div class="value" :class="uploadStatus.type">{{ uploadStatus.message }}</div>
        </div>
        <div class="how-to">
          <div class="label">{{ $t('How to Upload Content') }}</div>
          <div class="how-to-instructions">
            <ol class="f5">
              <li>
                {{ $t('Download our') }}
                <a
                  class="link-style"
                  href="https://developer-documentation.springboardvr.com/master/installing.html"
                  target="_blank"
                  rel="noopener"
                >{{ $t('command line tool.') }}</a>
              </li>
              <li>
                {{ $t('Follow the steps in the') }}
                <a
                  class="link-style"
                  href="https://developer-documentation.springboardvr.com/master/"
                  target="_blank"
                  rel="noopener"
                >{{ $t('Content Upload Guide') }}</a>
                {{ $t('to upload your content our servers.') }}
              </li>
            </ol>
            <u-i-button
              href="https://developer-documentation.springboardvr.com/master/"
              target="_blank"
              rel="nofollow"
            >
              <u-i-icon name="external-link" />
              {{ $t('Content Upload Guide') }}
            </u-i-button>
          </div>
        </div>
      </div>
    </ContentBlock>
    <ContentBlock>
      <div slot="instructions">
        <h5>{{ $t('Digital Rights Management') }}</h5>
        <i18n
          tag="p"
          path="We offer a {DRM} (DRM) solution that we can automatically use for your content to reduce piracy."
        >
          <a
            class="link-style"
            slot="DRM"
            href="https://developer-documentation.springboardvr.com/master/drm.html"
            target="_blank"
          >{{ $t("wrapper based Digital Rights Management") }}</a>
        </i18n>
      </div>
      <div class="how-to">
        <UISwitch v-model="contentOwnership.drm" :title="$t('Enable our DRM solution')" />
      </div>
    </ContentBlock>
  </div>
</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import ContentBlock from '@/core/shared/components/ContentBlock';
import UIButton from '@/core/shared/components/ui/UIButton';
import UIIcon from '@/core/shared/components/ui/UIIcon';
import UISwitch from '@/core/shared/components/ui/UISwitch';

@Component({
  components: {
    ContentBlock,
    UIButton,
    UIIcon,
    UISwitch,
  },
  props: {
    contentOwnership: {
      type: Object,
      required: true,
    },
  },
})
export default class CDS extends Vue {
  get uploadStatus () {
    const { approvalStatus, cds } = this.contentOwnership;
    if (!cds) {
      return {
        type: 'info',
        message: this.$t('No Content Uploaded'),
      };
    } else if (approvalStatus === 'rejected') {
      return {
        type: 'error',
        message: this.$t('Content rejected, see email for details'),
      };
    } else if (approvalStatus === 'pending') {
      return {
        type: 'warning',
        message: this.$t('Content uploaded, review in progress'),
      };
    } else if (approvalStatus === 'approved') {
      return {
        type: 'success',
        message: this.$t('Content uploaded & approved'),
      };
    }
  }
}
</script>

<style lang="postcss" scoped>
@import "core/shared/styles";

.label {
  @apply --f6;

  margin-bottom: var(--spacingSm);
}

.status {
  margin-bottom: var(--spacingMd);

  & .value {
    @apply --f3;

    &.info {
      color: var(--colorInfoFlat);
    }
    &.success {
      color: var(--colorSuccessFlat);
    }
    &.error {
      color: var(--colorSBRedFlat);
    }
    &.warning {
      color: var(--colorErrorFlat);
    }
  }
}

ol {
  list-style-type: decimal;
  padding-left: var(--spacingSm);
  margin-right: var(--spacingSm);

  & li {
    margin-bottom: var(--spacingXS);
  }
}

.how-to-instructions {
  align-items: center;

  @media (--tablet) {
    display: flex;
  }

  & p {
    @apply --f5;

    @media (--tablet) {
      flex: 0 0 50%;
      margin-right: var(--spacingLg);
    }

    & a {
      @apply --buttonLink;
    }
  }

  & > a {
    width: 100%;
  }
}
</style>
